import { useMemo } from "react";

import { useTheme } from "react-native-paper";

import { useGameplayStore } from "../../game/stores/useGameplayStore";
import { deriveStreak } from "../../game/streak";
import { useAssetStore } from "../../stores/useAssetStore";
import { useGameStore } from "../../stores/useGameStoreFacade";
import type { ExtendedTheme } from "../../theme/SynapseTheme";
import type { GameReport, MoveJudgement } from "../../utils/gameReportUtils";
import type { BoardInput, BoardPalette, GameStatus } from "./boardModel";
import { optionWords } from "./options";

export interface DisplayOverride {
  player?: boolean;
  optimal?: boolean;
  suggested?: boolean;
  ai?: boolean;
}

export interface BoardData {
  input: BoardInput | null;
  graphData: ReturnType<typeof useAssetStore.getState>["graphData"];
  status: GameStatus;
  /** Consecutive Local Optimal moves (live: from useGameplayStore). */
  streak: number;
  /** A live game (not a report replay): moves animate and nodes are tappable. */
  live: boolean;
  selectWord: (word: string) => void;
}

/**
 * Collects the board's inputs from the live game stores, or from a finished
 * game's report for the report mini-graph.
 */
export function useBoardData(
  gameReport?: GameReport,
  override?: DisplayOverride,
): BoardData {
  const { customColors } = useTheme() as ExtendedTheme;
  const graphData = useAssetStore((s) => s.graphData);
  const store = {
    startWord: useGameStore((s) => s.startWord),
    targetWord: useGameStore((s) => s.targetWord),
    currentWord: useGameStore((s) => s.currentWord),
    playerPath: useGameStore((s) => s.playerPath),
    status: useGameStore((s) => s.gameStatus),
    optimalPath: useGameStore((s) => s.optimalPath),
    suggestedPath: useGameStore((s) => s.suggestedPathFromCurrent),
    aiPath: useGameStore((s) => s.aiPath),
    display: useGameStore((s) => s.pathDisplayMode),
    optimalChoices: useGameStore((s) => s.optimalChoices),
  };
  const selectWord = useGameStore((s) => s.selectWord);
  const liveStreak = useGameplayStore((s) => s.streak);

  const palette: BoardPalette = useMemo(
    () => ({
      start: customColors.startNode,
      target: customColors.endNode,
      current: customColors.currentNode,
      path: customColors.pathNode,
      gold: customColors.globalOptimalNode,
      purple: customColors.localOptimalNode,
      ai: customColors.aiPath,
    }),
    [customColors],
  );

  const r = gameReport;
  const status: GameStatus = r ? r.status : store.status;
  const display = useMemo(
    () => ({
      player: override?.player ?? store.display.player,
      optimal: override?.optimal ?? store.display.optimal,
      suggested: override?.suggested ?? store.display.suggested,
      ai: override?.ai ?? store.display.ai,
    }),
    [override, store.display],
  );

  const input = useMemo<BoardInput | null>(() => {
    const startWord = r ? r.startWord : store.startWord;
    const targetWord = r ? r.targetWord : store.targetWord;
    if (!graphData || !startWord || !targetWord) return null;
    if (status === "idle" || status === "loading") return null;
    const playerPath = r ? r.playerPath : store.playerPath;
    // A report shows where a won game ended; a given-up report has no current.
    const currentWord = r
      ? r.status === "won"
        ? r.playerPath[r.playerPath.length - 1]
        : null
      : store.currentWord;
    const optimalChoices = (r ? r.optimalChoices : store.optimalChoices) as
      | MoveJudgement[]
      | undefined;
    return {
      position: (w) => (graphData[w]?.tsne as [number, number]) ?? null,
      startWord,
      targetWord,
      currentWord,
      playerPath,
      optimalPath: (r ? r.optimalPath : store.optimalPath) ?? [],
      suggestedPath: (r ? r.suggestedPath : store.suggestedPath) ?? [],
      aiPath: (r ? r.aiPath : store.aiPath) ?? [],
      optimalChoices: optimalChoices ?? [],
      edgeLevels: deriveStreak(optimalChoices).levels,
      // The live board's next-word options: the word buttons' list.
      options: r ? [] : optionWords(graphData, currentWord),
      status,
      display,
      palette,
    };
  }, [
    r,
    graphData,
    store.startWord,
    store.targetWord,
    store.currentWord,
    store.playerPath,
    store.optimalPath,
    store.suggestedPath,
    store.aiPath,
    store.optimalChoices,
    status,
    display,
    palette,
  ]);

  const streak = r ? deriveStreak(r.optimalChoices).streak : liveStreak;
  return { input, graphData, status, streak, live: !r, selectWord };
}
